import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { Typography } from '@mui/material';
import Lightbox from 'yet-another-react-lightbox';
import PhotoAlbum, { RenderRowContainerProps } from 'react-photo-album';

import { DELETE_FIND_IMAGE_MUTATION, reconcileFindsInCache } from '../../gql';
import {
  ImageDeleteButton,
  imageDeleteRevealStyles,
} from '../ImageDeleteButton';
import { ImageThumbnail } from '../design-system';
import { FormattedMessage, useIntl } from 'react-intl';

const THUMBNAIL_SIZE = 150;
const animationSettings = { fade: 150, swipe: 0 };
const noImagesIconStyles = { mx: 1, verticalAlign: 'text-top' };

const photoWrapperStyles = {
  ...imageDeleteRevealStyles,
  maxWidth: THUMBNAIL_SIZE,
  maxHeight: THUMBNAIL_SIZE,
};

const renderRowContainer = ({
  rowContainerProps,
  children,
}: RenderRowContainerProps) => (
  <div
    {...rowContainerProps}
    style={{
      ...(rowContainerProps?.style || {}),
      justifyContent: 'flex-start',
    }}
  >
    {children}
  </div>
);

const ImageList = ({ findId, images, amcrImages, mainImage, readOnly }) => {
  const intl = useIntl();
  const [index, setIndex] = useState(-1);
  const [deleteFindImage, { loading: deleting }] = useMutation(
    DELETE_FIND_IMAGE_MUTATION,
    {
      update: (cache, { data }) => {
        const updatedFind = data?.deleteFindImage?.find;

        if (updatedFind) {
          reconcileFindsInCache(cache, [updatedFind.id]);
        }
      },
    }
  );

  const [slides, thumbs] = useMemo(
    () => [
      [
        ...(images || []).map((i, index) => ({
          src: `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${i}/public`,
        })),
        ...(amcrImages || []).map((i, index) => ({
          src: `https://digiarchiv.aiscr.cz/api/img/medium?size=medium&id=${i}`,
        })),
      ],
      [
        ...(images || []).map((i, index) => ({
          src: `https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw/${i}/w=150`,
          key: i,
          width: THUMBNAIL_SIZE,
          height: THUMBNAIL_SIZE,
          meta: {
            index,
            // Only images in our own storage can be deleted, AMCR images are
            // owned by the archive.
            imageId: i,
          },
        })),
        ...(amcrImages || []).map((i, index) => ({
          src: `https://digiarchiv.aiscr.cz/api/img/thumb?id=${i}`,
          key: i,
          width: THUMBNAIL_SIZE,
          height: THUMBNAIL_SIZE,
          meta: {
            index: index + (images || []).length,
            imageId: undefined,
          },
        })),
      ],
    ],
    [images, amcrImages]
  );
  const lightboxLabels = useMemo(
    () => ({
      Close: intl.formatMessage({
        id: 'action.close',
        defaultMessage: 'Close',
      }),
      Previous: intl.formatMessage({
        id: 'image.previous',
        defaultMessage: 'Previous image',
      }),
      Next: intl.formatMessage({
        id: 'image.next',
        defaultMessage: 'Next image',
      }),
    }),
    [intl]
  );

  const handleThumbnailClick = ({ index }) => {
    setIndex(index);
  };

  const handleDelete = useCallback(
    async (imageId: string) => {
      if (
        !window.confirm(
          intl.formatMessage({
            id: 'image.delete.confirm',
            defaultMessage: 'Delete this image? This cannot be undone.',
          })
        )
      ) {
        return;
      }

      setIndex(-1);

      try {
        const { data } = await deleteFindImage({
          variables: { findId, imageId },
        });

        if (!data?.deleteFindImage?.find) {
          throw new Error('The image could not be deleted.');
        }
      } catch (error) {
        console.error(error);
        window.alert(
          intl.formatMessage({
            id: 'image.delete.error',
            defaultMessage: 'Deleting the image failed. Please try again.',
          })
        );
      }
    },
    [deleteFindImage, findId, intl]
  );

  const renderPhoto = useCallback(
    ({
      photo,
      imageProps: { alt, style, ...restImageProps },
      wrapperStyle,
    }) => (
      <ImageThumbnail
        alt={alt}
        imageProps={restImageProps}
        size={THUMBNAIL_SIZE}
        src={photo.src}
        sx={photoWrapperStyles}
        style={wrapperStyle}
      >
        {!readOnly && !!findId && !!photo.meta?.imageId && (
          <ImageDeleteButton
            disabled={deleting}
            onDelete={() => handleDelete(photo.meta.imageId)}
          />
        )}
      </ImageThumbnail>
    ),
    [deleting, findId, handleDelete, readOnly]
  );

  return slides.length === 0 ? (
    <Typography my={2} textAlign="center" color="text.secondary">
      <TipsAndUpdatesIcon sx={noImagesIconStyles} />
      <FormattedMessage
        id="image.noneUploadedYet"
        defaultMessage="No images uploaded yet."
      />
    </Typography>
  ) : (
    <>
      <PhotoAlbum
        targetRowHeight={THUMBNAIL_SIZE}
        padding={5}
        spacing={5}
        layout="rows"
        photos={thumbs}
        onClick={handleThumbnailClick}
        renderPhoto={renderPhoto}
        renderRowContainer={renderRowContainer}
      />
      <Lightbox
        animation={animationSettings}
        index={index}
        slides={slides}
        labels={lightboxLabels}
        open={index >= 0}
        close={() => setIndex(-1)}
      />
    </>
  );
};

export { ImageList };
